/**
 * Servicio de planes y límites para clientes (Básica / Premium).
 * Consultas de patente/API deshabilitadas (plateChecksMaxMonthly = 0 desde getClientPlanLimits).
 */
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  runTransaction,
  collection,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { getClientPlanLimits } from '../../../constants/membershipPlans';

const USUARIOS = 'usuarios';
const PLATE_CHECKS_LOGS = 'plate_checks_logs';

/** Mes actual en formato YYYY-MM */
export function currentMonth() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * Inicializa plan/límites/usage en usuarios/{uid} si no existen (merge).
 * Solo escribe plan/limits/usage si faltan; no sobrescribe si ya existen.
 * @param {string} uid
 * @returns {Promise<{ plan: string, limits: object }>}
 */
export async function ensureUsuarioPlanLimits(uid) {
  if (!uid) return { plan: 'basic', limits: getClientPlanLimits('basic') };
  const ref = doc(db, USUARIOS, uid);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const plan = (data.plan || 'basic').toLowerCase();
  const limits = data.limits || getClientPlanLimits(plan);

  const updates = {};
  if (!data.plan) updates.plan = 'basic';
  if (!data.limits) updates.limits = getClientPlanLimits(updates.plan || plan);
  const usage = data.usage || {};
  const pc = usage.plateChecks || {};
  const month = currentMonth();
  if (pc.month !== month || typeof pc.used !== 'number') {
    updates['usage.plateChecks'] = { month, used: 0 };
  }
  updates.updatedAt = serverTimestamp();

  if (Object.keys(updates).length > 1 || (Object.keys(updates).length === 1 && !('updatedAt' in updates))) {
    if (snap.exists()) {
      await updateDoc(ref, {
        ...(updates.plan !== undefined && { plan: updates.plan }),
        ...(updates.limits !== undefined && { limits: updates.limits }),
        ...(updates['usage.plateChecks'] !== undefined && { usage: { ...usage, plateChecks: updates['usage.plateChecks'] } }),
        updatedAt: updates.updatedAt
      });
    } else {
      // Crear documento por primera vez (plan/limits/usage por defecto)
      await setDoc(ref, {
        plan: updates.plan || 'basic',
        limits: updates.limits || getClientPlanLimits('basic'),
        usage: { plateChecks: updates['usage.plateChecks'] || { month: currentMonth(), used: 0 } },
        updatedAt: updates.updatedAt
      }, { merge: true });
    }
  }

  return { plan: updates.plan || plan, limits: updates.limits || limits };
}

/**
 * Obtiene uso del cliente: plan, límites y contadores.
 * Límites: Gratuito 2 veh, 10 gastos/mes; Premium 10 veh, gastos ilimitados.
 * @param {string} uid
 * @param {number} vehiclesCount - cantidad de vehículos del usuario (ya contados por quien llama)
 * @param {number} [gastosCountThisMonth] - gastos registrados en el mes actual (quien llama puede pasarlo para validar límite)
 * @returns {Promise<{ plan, vehiclesMax, vehiclesCount, plateChecksMaxMonthly, plateChecksUsed, plateChecksMonth, plateChecksRemaining, maxGastosMonthly, gastosRemaining }>}
 */
export async function getUsuarioUsage(uid, vehiclesCount = 0, gastosCountThisMonth = undefined) {
  const limitsBasic = getClientPlanLimits('basic');
  const limitsPremium = getClientPlanLimits('premium');
  if (!uid) {
    return {
      plan: 'basic',
      vehiclesMax: limitsBasic.vehiclesMax,
      vehiclesCount: 0,
      plateChecksMaxMonthly: limitsBasic.plateChecksMaxMonthly,
      plateChecksUsed: 0,
      plateChecksMonth: currentMonth(),
      plateChecksRemaining: limitsBasic.plateChecksMaxMonthly,
      maxGastosMonthly: limitsBasic.maxGastosMonthly,
      gastosRemaining: limitsBasic.maxGastosMonthly
    };
  }
  const ref = doc(db, USUARIOS, uid);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const plan = (data.plan || 'basic').toLowerCase();
  const limits = data.limits || getClientPlanLimits(plan);
  const vehiclesMax = limits.vehiclesMax ?? (plan === 'premium' ? 10 : 2);
  const plateChecksMaxMonthly = getClientPlanLimits(plan).plateChecksMaxMonthly;
  const maxGastosMonthly = limits.maxGastosMonthly ?? (plan === 'premium' ? -1 : 10);
  const usage = data.usage || {};
  const pc = usage.plateChecks || {};
  const month = currentMonth();
  const used = pc.month === month ? (pc.used || 0) : 0;
  const plateChecksRemaining = Math.max(0, plateChecksMaxMonthly - used);
  const gastosUsed = typeof gastosCountThisMonth === 'number' ? gastosCountThisMonth : 0;
  const gastosRemaining = maxGastosMonthly < 0 ? -1 : Math.max(0, maxGastosMonthly - gastosUsed);

  return {
    plan,
    vehiclesMax,
    vehiclesCount,
    plateChecksMaxMonthly,
    plateChecksUsed: used,
    plateChecksMonth: month,
    plateChecksRemaining,
    maxGastosMonthly,
    gastosRemaining: maxGastosMonthly < 0 ? -1 : gastosRemaining
  };
}

/**
 * Comprueba si el usuario puede hacer una consulta de patente (sin consumir).
 * @param {string} uid
 * @returns {Promise<{ allowed: boolean, used: number, max: number, remaining: number, month: string }>}
 */
export async function checkPlateCheckLimit(uid) {
  const u = await getUsuarioUsage(uid, 0);
  return {
    allowed: u.plateChecksRemaining > 0,
    used: u.plateChecksUsed,
    max: u.plateChecksMaxMonthly,
    remaining: u.plateChecksRemaining,
    month: u.plateChecksMonth
  };
}

/**
 * Consume una consulta de patente: transaction que incrementa usage y escribe en plate_checks_logs.
 * Debe llamarse por intento (antes o justo después de la llamada a la API) para evitar abuso.
 * @param {string} uid
 * @param {string} plate - patente consultada
 * @param {string} provider - ej. 'apis-chile' o 'boostr'
 * @param {{ success: boolean, errorCode?: string, responseTimeMs?: number }} result
 * @returns {Promise<{ success: boolean, remaining: number }>}
 */
export async function consumePlateCheck(uid, plate, provider = 'apis-chile', result = { success: false }) {
  const month = currentMonth();
  const userRef = doc(db, USUARIOS, uid);

  let remaining = 0;
  await runTransaction(db, async (tx) => {
    const userSnap = await tx.get(userRef);
    const data = userSnap.exists() ? userSnap.data() : {};
    const plan = (data.plan || 'basic').toLowerCase();
    const max = getClientPlanLimits(plan).plateChecksMaxMonthly;
    const usage = data.usage || {};
    let pc = usage.plateChecks || {};
    if (pc.month !== month) pc = { month, used: 0 };
    if (pc.used >= max) {
      throw new Error(`Has alcanzado tu límite de consultas este mes (${pc.used}/${max}). Se reinicia el 1 del próximo mes.`);
    }
    const newUsed = pc.used + 1;
    remaining = Math.max(0, max - newUsed);
    tx.update(userRef, {
      usage: { ...usage, plateChecks: { month: pc.month, used: newUsed } },
      updatedAt: serverTimestamp()
    });
  });
  const canContinue = true;

  const logRef = collection(db, PLATE_CHECKS_LOGS);
  await addDoc(logRef, {
    uid,
    plate: (plate || '').trim().toUpperCase(),
    createdAt: serverTimestamp(),
    month,
    provider: provider || 'apis-chile',
    success: result.success !== false,
    errorCode: result.errorCode || null,
    responseTimeMs: result.responseTimeMs ?? null
  });

  return { success: canContinue, remaining };
}

export { getClientPlanLimits };
